import { cloneDeep, set, forEach, omit, findIndex } from "@utils/lodash";
import utils from "@utils";
import { push } from "react-router-redux";
import GlobalsActions from "@redux/globals";
import AppActions from "@redux/app";

export const updateStateGeneric = ({ state, property, value }) => {
	const newState = cloneDeep(state);

	// Update state
	set(newState, property, value);

	return newState;
};

export const updateProgressButton =
	({ module, property, value, callback }) =>
	dispatch => {
		dispatch(GlobalsActions.changeState(module, property, value));

		// Prevent clean up if it's loading
		if (value === "loading") {
			if (callback) {
				callback();
			}

			return;
		}

		// Clean progressButton after a 1000ms to let component finish the animation
		setTimeout(() => {
			dispatch(GlobalsActions.changeState(module, property, ""));

			if (callback) {
				callback();
			}
		}, 800);
	};

export const apiErrorHandler =
	({
		dispatch,
		module = "app",
		property = ["loaders", "app"],
		usingProgressButton = false,
		defaultTitle = "Oops",
		defaultMessage = "Something went wrong",
		hideAlert = false,
	}) =>
	error => {
		utils.genericAPIErrorHandler(
			defaultMessage,
			error,
			property,
			undefined,
			undefined,
			defaultTitle,
			hideAlert,
		);

		if (dispatch) {
			// Stop loading animation
			if (!usingProgressButton) {
				dispatch(GlobalsActions.changeState(module, property, false));
			} else {
				dispatch(updateProgressButton({ module, property, value: "error" }));
			}
		}
	};

export const processUrl =
	(callback, omitParseQueries = []) =>
	(dispatch, getState) => {
		const {
			routing: {
				params,
				locationBeforeTransitions: { pathname, query: rawQuery },
			},
			app: { subName },
		} = getState();
		const nonIntegerQueries = ["startDate", "endDate", ...omitParseQueries];
		const dataCollection = [];
		const filtersCollection = [];

		const query = cloneDeep(rawQuery);
		/**
		 * If we change the viewType
		 * we set the query param to
		 * the initial page and push
		 * the url without the page param
		 * in order to get the first.
		 *
		 * Finally we save the last active
		 * page in order to check that in
		 * the future
		 */

		if (params.viewType && subName !== params.viewType) {
			query.pageNumber = 1;
			// Update URL
			dispatch(
				push({
					pathname,
					query: { ...omit(query, ["page", "pageNumber"]) },
				}),
			);
			// Update last active page
			dispatch(AppActions.updateSubName(params.viewType));
		}

		/**
		 * Apply query params
		 * as filters including
		 * alias such as
		 * (page, and displayCount)
		 */
		forEach(query, (rawValue, key) => {
			let property;
			let value = rawValue;

			switch (key) {
				/**
				 * Delete default
				 * initial filters
				 * to be replaced
				 * with the query
				 * value
				 */
				case "calendarView":
				case "startDate":
				case "endDate":
				case "dateRange": {
					property = key;

					/**
					 * Get default filter
					 * index and delete it
					 */
					const filterIndex = findIndex(filtersCollection, ["property", property]);

					if (filterIndex > -1) {
						filtersCollection.splice(filterIndex, 1);
					}
					break;
				}
				default:
					property = key;
					break;
			}

			// Parse booleans
			if (value === "true" || value === "false") {
				value = value === "true";
			}

			// Parse integers
			if (!nonIntegerQueries.includes(key) && !Number.isNaN(parseInt(value, 10))) {
				value = parseInt(value, 10);
			}

			filtersCollection.push({ property, value });
		});

		// Reset expandedRowIds
		dataCollection.push({ property: "expandedRowIds", value: [] });

		callback(filtersCollection, dataCollection);
	};

export const applyCriteria =
	(filters, initialFilters, criteriaCollection, callback, customQueryProcessor = {}) =>
	(dispatch, getState) => {
		const {
			routing: {
				locationBeforeTransitions: { pathname: rawPathname, query: oldQuery },
			},
		} = getState();
		let newQuery = cloneDeep(oldQuery);
		let pathname = cloneDeep(rawPathname);
		let newFiltersCollection = [];
		let newDataCollection = [];

		const queryProcessor = {
			pageNumber: ({
				value,
				pathName,
				dataCollection,
				filtersCollection,
				query: rawQuery,
			}) => {
				const query = cloneDeep(rawQuery);
				query.pageNumber = value;

				/**
				 * Remove page from
				 * query params if
				 * it's the default
				 * value
				 */
				if (value === initialFilters.pageNumber) {
					delete query.pageNumber;
				}

				return {
					pathName,
					dataCollection,
					filtersCollection,
					query,
				};
			},
			displayCount: ({
				value,
				pathName,
				dataCollection,
				filtersCollection,
				query: rawQuery,
			}) => {
				const query = cloneDeep(rawQuery);
				query.displayCount = value;

				/**
				 * Remove show from
				 * query params if
				 * it's the default
				 * value
				 */
				if (value === initialFilters.displayCount) {
					delete query.displayCount;
				}

				return {
					pathName,
					dataCollection,
					filtersCollection,
					query,
				};
			},
			search: ({ value, pathName, dataCollection, filtersCollection, query: rawQuery }) => {
				const query = cloneDeep(rawQuery);
				query.search = value;

				/**
				 * Remove search from
				 * query params if
				 * it's the default
				 * value
				 */
				if (value === initialFilters.search) {
					delete query.search;
				}

				return {
					pathName,
					dataCollection,
					filtersCollection,
					query,
				};
			},

			dateRange: ({
				value,
				pathName,
				dataCollection,
				filtersCollection,
				query: rawQuery,
			}) => {
				const query = cloneDeep(rawQuery);
				/**
				 * Remove dateRange from
				 * query params if
				 * it's the default
				 * value
				 */
				if (value === 7) {
					delete query.dateRange;
				}

				if (value !== "custom") {
					delete query.startDate;
					delete query.endDate;

					filtersCollection.push({ property: "startDate", value: "" });
					filtersCollection.push({ property: "endDate", value: "" });
				}

				return {
					pathName,
					dataCollection,
					filtersCollection,
					query,
				};
			},

			sortBy: ({ value, pathName, dataCollection, filtersCollection, query: rawQuery }) => {
				const query = cloneDeep(rawQuery);
				const newDirection = filters.sortBy === value ? !filters.asc : true;
				query.asc = newDirection;

				/**
				 * Remove sortBy from
				 * query params if
				 * it's the default
				 * value
				 */
				if (value === initialFilters.sortBy) {
					delete query.sortBy;
				}

				/**
				 * Remove asc from
				 * query params if
				 * it's the default
				 * value
				 */
				if (newDirection) {
					delete query.asc;
				}

				filtersCollection.push({ property: "asc", value: newDirection });

				return {
					pathName,
					dataCollection,
					filtersCollection,
					query,
				};
			},
			/**
			 * We attach the custom query
			 * processors if there is
			 * any
			 */
			...customQueryProcessor,
		};

		// Process criteriaCollection
		forEach(criteriaCollection, ({ property, value }) => {
			// Add criteria to filters
			newFiltersCollection.push({ property, value });

			// Update query param
			newQuery[property] = value;

			/**
			 * Update related query
			 * params, and apply alias
			 */
			if (property in queryProcessor) {
				const result = queryProcessor[property]({
					value,
					pathName: rawPathname,
					dataCollection: newDataCollection,
					filtersCollection: newFiltersCollection,
					query: newQuery,
				});

				pathname = result.pathName;
				newDataCollection = result.dataCollection;
				newFiltersCollection = result.filtersCollection;
				newQuery = result.query;
			}

			/**
			 * Reset pageNumber on
			 * every filter except
			 * for pageNumber
			 */
			if (property !== "pageNumber") {
				delete newQuery.pageNumber;
				newFiltersCollection.push({ property: "pageNumber", value: 1 });
			}

			/**
			 * Delete empty query params
			 * if there are any
			 */
			if (
				(property !== "pageNumber" ||
					property !== "displayCount" ||
					property !== "sortBy") &&
				value === null
			) {
				delete newQuery[property];
			}
		});

		// Update URL
		dispatch(
			push({
				pathname,
				query: newQuery,
			}),
		);

		/**
		 * We call the callback function
		 * to send the collections to the
		 * corresponding module
		 */

		callback({ filtersCollection: newFiltersCollection, dataCollection: newDataCollection });
	};

export default {
	updateStateGeneric,
	apiErrorHandler,
	updateProgressButton,
	applyCriteria,
	processUrl,
};
